import { ChevronLeft, Plus } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  AssignmentForm,
  AssignmentList,
  type AssignmentRole,
  type Client,
  type CreateAssignmentInput,
  type UserAssignment,
} from "@lexiun/org-admin";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { usePlatformApi } from "../hooks/usePlatformApi";
import { PlatformApiError } from "../lib/platform-api";
import { clientRowToUserAssignment } from "../lib/assignment-mappers";

/**
 * Client-level assignments admin page (Phase D/D4).
 *
 * Mounted at `/orgs/:orgId/clients/:clientId/assignments`. Lists
 * direct `ClientUserAssignment` rows via Phase B lexserver routes
 * and renders them through `@lexiun/org-admin`'s `AssignmentList` +
 * `AssignmentForm`. There is no parent scope to inherit from at the
 * client layer (the parent is Org, and org-level access goes through
 * ORG_ADMIN, not a row in any assignment table), so every row at
 * this scope has `source: "direct"`.
 */
export function ClientAssignments() {
  const { orgId = "", clientId = "" } = useParams<{
    orgId: string;
    clientId: string;
  }>();
  const api = usePlatformApi();

  const [client, setClient] = useState<Client | null>(null);
  const [assignments, setAssignments] = useState<UserAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!orgId || !clientId) return;
    try {
      setLoading(true);
      const [clientRes, rowsRes] = await Promise.all([
        api.getClient(orgId, clientId),
        api.listClientAssignments(orgId, clientId),
      ]);
      setClient(clientRes.data);
      setAssignments(rowsRes.data.map(clientRowToUserAssignment));
      setError(null);
    } catch (e) {
      setError(
        e instanceof PlatformApiError
          ? `${e.code}: ${e.message}`
          : e instanceof Error
            ? e.message
            : "Failed to load client assignments",
      );
    } finally {
      setLoading(false);
    }
  }, [api, orgId, clientId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCreate = async (input: CreateAssignmentInput) => {
    setSubmitting(true);
    setError(null);
    try {
      await api.createClientAssignment(orgId, clientId, input);
      setShowCreate(false);
      await load();
    } catch (e) {
      setError(
        e instanceof PlatformApiError
          ? `${e.code}: ${e.message}`
          : e instanceof Error
            ? e.message
            : "Failed to create client assignment",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateRole = async (userId: string, role: AssignmentRole) => {
    setError(null);
    try {
      await api.updateClientAssignment(orgId, clientId, userId, { role });
      await load();
    } catch (e) {
      setError(
        e instanceof PlatformApiError
          ? `${e.code}: ${e.message}`
          : e instanceof Error
            ? e.message
            : "Failed to update assignment",
      );
    }
  };

  const handleDelete = async (userId: string) => {
    const ok = window.confirm(
      `Revoke assignment for user "${userId}"? They'll lose cascade access to every matter and case under this client.`,
    );
    if (!ok) return;
    setDeleteLoading(userId);
    setError(null);
    try {
      await api.deleteClientAssignment(orgId, clientId, userId);
      await load();
    } catch (e) {
      setError(
        e instanceof PlatformApiError
          ? `${e.code}: ${e.message}`
          : e instanceof Error
            ? e.message
            : "Failed to revoke assignment",
      );
    } finally {
      setDeleteLoading(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="client-assignments-page">
      <header className="flex items-center justify-between">
        <div>
          <Link
            to={`/orgs/${orgId}/clients`}
            className="mb-2 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
          >
            <ChevronLeft className="h-3 w-3" /> Back to clients
          </Link>
          <h1 className="text-2xl font-semibold">
            {client ? (
              <>
                Assignments — {client.name}
                {/* Client @alias handle (read-only) — see
                    ai-docs/handoffs/client-alias-lexide-lexportal-relay-2026-07-06.md */}
                {client.alias && (
                  <span
                    className="ml-2 font-mono text-sm font-normal text-slate-400 dark:text-slate-500"
                    title="Client handle (@alias)"
                  >
                    @{client.alias}
                  </span>
                )}
              </>
            ) : (
              "Client assignments"
            )}
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {loading
              ? "Loading..."
              : `${assignments.length} direct assignment${assignments.length === 1 ? "" : "s"}`}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          disabled={!client || loading}
          className="inline-flex items-center gap-2 rounded bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          data-testid="client-assignments-create-button"
        >
          <Plus className="h-4 w-4" />
          Grant role
        </button>
      </header>

      {error && (
        <div
          role="alert"
          className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300"
          data-testid="client-assignments-error"
        >
          {error}
        </div>
      )}

      <div className="rounded border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
        <AssignmentList
          scope="client"
          assignments={assignments}
          loading={loading}
          onUpdateRole={handleUpdateRole}
          onDelete={handleDelete}
          deleteLoading={deleteLoading}
        />
      </div>

      <Dialog open={showCreate} onOpenChange={(o) => !o && setShowCreate(false)}>
        <DialogContent data-testid="client-assignments-create-dialog">
          <DialogHeader>
            <DialogTitle>Grant a client role</DialogTitle>
            <DialogDescription>
              The user will gain cascade access to every matter and case
              under {client?.name ?? "this client"}.
            </DialogDescription>
          </DialogHeader>
          <AssignmentForm
            scope="client"
            onSubmit={handleCreate}
            onCancel={() => setShowCreate(false)}
            submitting={submitting}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
